/**
 * ============================================================================
 *  LION BRAND YARN CA — Custom Mega Menu JS
 *  File: assets/mega-menu.js
 * ============================================================================
 *
 *  Desktop (≥ 1240 px)
 *    • Hover opens mega menu or flyout via CSS :root[data-lby-open] attribute.
 *      Mutations are made on <html>, NOT inside the nav, to avoid triggering
 *      Prestige's MutationObserver which watches .Header__MainNav subtree and
 *      causes a reflow → spurious mouseleave → flicker feedback loop.
 *    • 200ms grace period prevents accidental closes on cursor transitions.
 *    • Keyboard: Tab moves focus inside panel, Escape closes, arrow keys
 *      navigate tabs (ARIA tablist pattern), Enter/Space opens panel.
 *
 *  Mobile (< 1240 px)
 *    • Desktop nav is hidden. Prestige sidebar collapsibles handle mobile.
 *
 * ============================================================================
 */

(function () {
  'use strict';

  /* ── Config ──────────────────────────────────────────────── */
  const BREAKPOINT  = 1240;  // px — matches CSS breakpoint
  const HOVER_GRACE = 200;   // ms — grace period before closing

  /* ── State ───────────────────────────────────────────────── */
  let openItem   = null;
  let leaveTimer = null;

  /* ── Element refs ────────────────────────────────────────── */
  const nav      = document.getElementById('lby-main-nav');
  const backdrop = document.getElementById('lby-backdrop');

  if (!nav) return;

  /* ── Helpers ─────────────────────────────────────────────── */

  function isDesktop() {
    return window.innerWidth >= BREAKPOINT;
  }

  /** Show a panel via :root[data-lby-open] — mutates <html>, not the nav,
   *  so Prestige's MutationObserver on .Header__MainNav never fires */
  function showViaRoot(panelId, item) {
    // If no tab is active (deactivateAllTabs ran last time), reset to first tab
    // before showing the panel so content is never blank on re-open.
    if (item) {
      const mega = item.querySelector('.lby-mega');
      if (mega) {
        const tabs   = Array.from(mega.querySelectorAll('.lby-mega__tab'));
        const panels = Array.from(mega.querySelectorAll('.lby-panel'));
        if (tabs.length && !tabs.some(t => t.classList.contains('is-active'))) {
          tabs.forEach((t, i) => {
            t.classList.toggle('is-active', i === 0);
            t.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
            t.setAttribute('tabindex', i === 0 ? '0' : '-1');
          });
          panels.forEach((p, i) => {
            p.classList.toggle('is-active', i === 0);
            p.hidden = i !== 0;
          });
        }
      }
    }
    if (panelId) document.documentElement.setAttribute('data-lby-open', panelId);

    if (openItem && openItem !== item) {
      const prevPanel   = openItem.querySelector('.lby-mega, .lby-flyout');
      const prevTrigger = openItem.querySelector('.lby-nav__link');
      if (prevPanel)   prevPanel.setAttribute('aria-hidden', 'true');
      if (prevTrigger) prevTrigger.setAttribute('aria-expanded', 'false');
    }

    const panel   = item.querySelector('.lby-mega, .lby-flyout');
    const trigger = item.querySelector('.lby-nav__link');
    if (panel)   panel.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    if (backdrop) {
      const header = document.getElementById('shopify-section-header');
      backdrop.style.top = header ? header.offsetHeight + 'px' : '0px';
      backdrop.classList.add('is-visible');
    }
    openItem = item;
  }

  function hideViaRoot() {
    document.documentElement.removeAttribute('data-lby-open');

    if (openItem) {
      const panel   = openItem.querySelector('.lby-mega, .lby-flyout');
      const trigger = openItem.querySelector('.lby-nav__link');
      if (panel)   panel.setAttribute('aria-hidden', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

    }

    if (backdrop) backdrop.classList.remove('is-visible');
    openItem = null;
  }

  /** Open a panel via is-open class — used for keyboard navigation only */
  function openPanel(item) {
    if (item === openItem) return;
    if (openItem) closePanel(openItem, false);

    openItem = item;
    const panel   = item.querySelector('.lby-mega, .lby-flyout');
    const trigger = item.querySelector('.lby-nav__link');
    if (!panel) return;

    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    item.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (backdrop) backdrop.classList.add('is-visible');
  }

  /** Close a panel opened via is-open class */
  function closePanel(item, clearOpen = true) {
    if (!item) return;
    const panel   = item.querySelector('.lby-mega, .lby-flyout');
    const trigger = item.querySelector('.lby-nav__link');

    if (panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    }
    item.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (clearOpen) {
      openItem = null;
      if (backdrop) backdrop.classList.remove('is-visible');
    }
  }

  /** Close everything */
  function closeAll() {
    document.documentElement.removeAttribute('data-lby-open');
    if (backdrop) backdrop.classList.remove('is-visible');
    if (openItem) closePanel(openItem);
    openItem = null;
  }

  /** Deactivate all tabs in a mega panel — called when cursor moves to
   *  sidebar areas that have no associated panel (divider, direct links, gaps) */
  function deactivateAllTabs(mega) {
    mega.querySelectorAll('.lby-mega__tab').forEach(t => {
      t.classList.remove('is-active');
      t.setAttribute('tabindex', '-1');
    });
    mega.querySelectorAll('.lby-panel').forEach(p => {
      p.classList.remove('is-active');
    });
  }

  /** Activate a tab within a mega menu tablist */
  function activateTab(tab) {
    const mega = tab.closest('.lby-mega');
    if (!mega) return;

    const tablist = mega.querySelector('.lby-mega__tablist');
    const allTabs = Array.from(tablist.querySelectorAll('.lby-mega__tab'));
    const panelId = tab.getAttribute('aria-controls');
    const panel   = mega.querySelector('#' + panelId);

    allTabs.forEach(t => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });

    mega.querySelectorAll('.lby-panel').forEach(p => {
      p.classList.remove('is-active');
      p.hidden = true;
    });

    tab.classList.add('is-active');
    tab.setAttribute('tabindex', '0');

    if (panel) {
      panel.classList.add('is-active');
      panel.hidden = false;
    }
  }


  /* ── Mouse events ────────────────────────────────────────── */

  nav.querySelectorAll('.lby-nav__item[data-lby-mega]').forEach(item => {
    const panel   = item.querySelector('.lby-mega, .lby-flyout');
    const panelId = panel ? panel.id : null;

    item.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      clearTimeout(leaveTimer);
      if (openItem && openItem !== item) closePanel(openItem, true);
      showViaRoot(panelId, item);
    });
    // No mouseleave on <li> — see nav mouseleave below.
  });

  // Two responsibilities:
  // 1. Close panel when cursor moves to a non-mega item (e.g. Clearance) or
  //    the empty nav bar space after the last item.
  // 2. Deactivate tabs when cursor enters sidebar areas that have no panel
  //    (the divider, direct links like Color Stories / All Yarns, or gaps).
  //    Handled here rather than with separate mouseenter listeners to avoid
  //    event-ordering race conditions with activateTab.
  nav.addEventListener('mouseover', e => {
    if (!isDesktop()) return;

    const megaItem = e.target.closest('.lby-nav__item[data-lby-mega]');
    if (!megaItem) {
      // Cursor is over Clearance, empty nav space, etc. — close the panel.
      clearTimeout(leaveTimer);
      hideViaRoot();
      return;
    }

    // Cursor is inside a mega item. Only deactivate tabs when the cursor is
    // specifically over a direct link or divider — not gaps between tabs.
    // Checking gaps caused the diagonal-movement problem where tabs cleared
    // while the cursor was crossing from a tab button into the panel content.
    const onDirectLink = e.target.closest('.lby-mega__direct-link');
    const onDivider    = e.target.closest('.lby-mega__divider');
    if (onDirectLink || onDivider) {
      const mega = e.target.closest('.lby-mega');
      if (mega) deactivateAllTabs(mega);
    }
  });

  // Close with grace period when cursor truly leaves both the nav bar AND panel.
  nav.addEventListener('mouseleave', () => {
    if (!isDesktop()) return;
    leaveTimer = setTimeout(hideViaRoot, HOVER_GRACE);
  });


  /* ── Tab hover/click ─────────────────────────────────────── */

  nav.querySelectorAll('.lby-mega__tab').forEach(tab => {
    tab.addEventListener('click',      () => activateTab(tab));
    tab.addEventListener('mouseenter', () => { if (isDesktop()) activateTab(tab); });
  });


  /* ── Keyboard navigation ─────────────────────────────────── */

  nav.addEventListener('keydown', e => {
    if (!isDesktop()) return;

    if (e.key === 'Escape') {
      const trigger = openItem && openItem.querySelector('.lby-nav__link');
      closeAll();
      if (trigger) trigger.focus();
      return;
    }

    if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
      const active = document.activeElement;
      if (!active || !active.classList.contains('lby-mega__tab')) return;

      const tablist = active.closest('.lby-mega__tablist');
      if (!tablist) return;

      const tabs = Array.from(tablist.querySelectorAll('.lby-mega__tab'));
      const idx  = tabs.indexOf(active);
      let next   = idx;

      if (e.key === 'ArrowDown') next = (idx + 1) % tabs.length;
      if (e.key === 'ArrowUp')   next = (idx - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home')      next = 0;
      if (e.key === 'End')       next = tabs.length - 1;

      if (next !== idx) {
        e.preventDefault();
        tabs[next].focus();
        activateTab(tabs[next]);
      }
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      const active = document.activeElement;
      if (!active) return;
      const item = active.closest('.lby-nav__item[data-lby-mega]');
      if (item && active.classList.contains('lby-nav__link')) {
        e.preventDefault();
        if (openItem === item) {
          closeAll();
        } else {
          openPanel(item);
          const first = item.querySelector('.lby-mega__tab, .lby-flyout__list a, .lby-mega__direct-link');
          if (first) first.focus();
        }
      }
    }
  });


  /* ── Outside click ───────────────────────────────────────── */

  if (backdrop) backdrop.addEventListener('click', closeAll);

  document.addEventListener('click', e => {
    if (!isDesktop()) return;
    if (!nav.contains(e.target)) closeAll();
  });


  /* ── Focus leaves nav → close ────────────────────────────── */

  // Clicking a non-focusable element (e.g. empty panel space) moves focus to
  // body, firing focusout and triggering closeAll even though the interaction
  // was inside the nav. Track mousedown inside nav to suppress that false close.
  let mousedownInsideNav = false;
  nav.addEventListener('mousedown', () => {
    mousedownInsideNav = true;
    setTimeout(() => { mousedownInsideNav = false; }, 200);
  });

  nav.addEventListener('focusout', () => {
    if (!isDesktop()) return;
    setTimeout(() => {
      if (mousedownInsideNav) return;
      if (!nav.contains(document.activeElement)) closeAll();
    }, 0);
  });


  /* ── Resize below breakpoint → close ────────────────────── */

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { if (!isDesktop()) closeAll(); }, 100);
  });

  /* ── Fix Prestige Collapsible rapid-toggle race condition ───────────────
   * Prestige's slideDown registers a transitionend that sets height='auto'.
   * On rapid close before that fires, it fires late — setting height='auto'
   * while aria-expanded='false', leaving content visible but clipped blank.
   *
   * Our transitionend listener registered BEFORE Prestige's (on page load)
   * so Prestige's runs after and overwrites us. MutationObserver fires in
   * the microtask queue immediately after Prestige sets height='auto',
   * before the next render frame — so we always run last and can correct it. */
  const collapsibleObserver = new MutationObserver(mutations => {
    mutations.forEach(m => {
      if (m.attributeName !== 'style') return;
      const inner = m.target;
      if (inner.style.height !== 'auto') return;
      const btn = inner.previousElementSibling;
      if (btn && btn.getAttribute('aria-expanded') === 'false') {
        inner.style.height   = '0px';
        inner.style.overflow = 'hidden';
      }
    });
  });

  document.querySelectorAll('#SidebarNav .Collapsible__Inner').forEach(inner => {
    collapsibleObserver.observe(inner, { attributes: true, attributeFilter: ['style'] });
  });

})();


/* ── Mobile: close + reset sibling top-level collapsibles on open ───────────
 *
 *  Our #SidebarNav listener fires BEFORE Prestige's document-level handler,
 *  so aria-expanded is still the PRE-click value:
 *    "false" = user is OPENING → close+reset all other top-level items
 *    "true"  = user is CLOSING → nothing to do
 *
 *  resetChildren() clears nested state so re-opening a section always starts
 *  fully collapsed — no sub-menus left open from a previous visit.
 *
 * ──────────────────────────────────────────────────────────────────────────*/
(function () {
  'use strict';

  function resetChildren(collapsible) {
    collapsible.querySelectorAll('.Collapsible').forEach(function (child) {
      var childBtn   = child.querySelector('.Collapsible__Button');
      var childInner = child.querySelector('.Collapsible__Inner');
      if (childBtn)   childBtn.setAttribute('aria-expanded', 'false');
      if (childInner) { childInner.style.height = '0'; childInner.style.overflow = 'hidden'; }
    });
  }

  function closeCollapsible(collapsible) {
    var btn   = collapsible.querySelector('.Collapsible__Button');
    var inner = collapsible.querySelector('.Collapsible__Inner');
    if (!btn || !inner) return;
    if (btn.getAttribute('aria-expanded') !== 'true') return;
    resetChildren(collapsible);
    btn.setAttribute('aria-expanded', 'false');
    inner.style.overflow = 'hidden';
    inner.style.height   = inner.scrollHeight + 'px';
    inner.offsetHeight;
    inner.style.height   = '0';
  }

  var sidebarNav = document.getElementById('SidebarNav');
  if (!sidebarNav) return;

  sidebarNav.addEventListener('click', function (e) {
    var btn = e.target.closest('.Collapsible__Button[data-action="toggle-collapsible"]');
    if (!btn) return;
    if (btn.closest('.Collapsible__Content')) return;
    if (btn.getAttribute('aria-expanded') !== 'false') return;
    var parentLi = btn.closest('li');
    if (!parentLi) return;
    Array.from(sidebarNav.children).forEach(function (li) {
      if (li === parentLi) return;
      var sibling = li.querySelector('.Collapsible');
      if (sibling) closeCollapsible(sibling);
    });
  });

  /* ── Fix Prestige Collapsible rapid-toggle race condition ───────────────
   * Prestige's slideDown registers a transitionend that sets height='auto'.
   * On rapid close before that fires, it fires late — setting height='auto'
   * while aria-expanded='false', leaving content visible but clipped blank.
   *
   * Our transitionend listener registered BEFORE Prestige's (on page load)
   * so Prestige's runs after and overwrites us. MutationObserver fires in
   * the microtask queue immediately after Prestige sets height='auto',
   * before the next render frame — so we always run last and can correct it. */
  const collapsibleObserver = new MutationObserver(mutations => {
    mutations.forEach(m => {
      if (m.attributeName !== 'style') return;
      const inner = m.target;
      if (inner.style.height !== 'auto') return;
      const btn = inner.previousElementSibling;
      if (btn && btn.getAttribute('aria-expanded') === 'false') {
        inner.style.height   = '0px';
        inner.style.overflow = 'hidden';
      }
    });
  });

  document.querySelectorAll('#SidebarNav .Collapsible__Inner').forEach(inner => {
    collapsibleObserver.observe(inner, { attributes: true, attributeFilter: ['style'] });
  });

})();
